import Dexie, { type EntityTable } from "dexie";
import type { ReviewGuide } from "../../shared/guide";
import type { ReviewEvent } from "../../shared/progress";
import type { ReviewVerdict } from "../../shared/progress";
import type { ChewConfig } from "../../shared/config";
import type { FixtureDescriptor, FixtureFiles } from "./fixtures";

export interface CachedFixture {
  id: string;
  descriptor: FixtureDescriptor;
  guide: ReviewGuide;
  /** Optional for cached reviews written before repository config was retained. */
  config?: ChewConfig;
  files?: FixtureFiles;
  cachedAt: string;
}

export interface LocalReviewSession {
  id: string;
  fixtureId: string;
  guideHeadSha: string;
  startedAt: string;
  lastOpenedAt: string;
  activeLayerId?: string;
  activeFocusId?: string;
  layerViewStates?: Record<string, ReviewLayerViewState>;
  reviewVerdict?: ReviewVerdict;
  outcomeSummary?: string;
  completedAt?: string;
  activeDurationMs?: number;
}

export interface ReviewLayerViewState {
  selectedPath?: string;
  collapsedPaths: string[];
  scrollTop: number;
  updatedAt: string;
}

export interface DraftComment {
  id: string;
  sessionId: string;
  itemId: string;
  anchor?: DraftAnchor;
  /** Optional for compatibility with drafts saved before typed feedback existed. */
  kind?: DraftCommentKind;
  body: string;
  updatedAt: string;
}

export const draftCommentKinds = ["note", "question", "tip", "change"] as const;

export type DraftCommentKind = (typeof draftCommentKinds)[number];

export function draftCommentKind(draft: Pick<DraftComment, "kind">): DraftCommentKind {
  return draft.kind ?? "note";
}

export type DraftAnchor = {
  kind: "line";
  path: string;
  side: "LEFT" | "RIGHT";
  /** Last line of the anchor, matching GitHub's multi-line comment semantics. */
  line: number;
  /** First line when the anchor spans a range; absent for a single line. */
  startLine?: number;
};

/** Normalised span for an anchor, collapsing the single-line case onto one row. */
export function draftAnchorSpan(anchor: DraftAnchor): { startLine: number; endLine: number } {
  const startLine = anchor.startLine ?? anchor.line;
  return startLine <= anchor.line
    ? { startLine, endLine: anchor.line }
    : { startLine: anchor.line, endLine: startLine };
}

export function lineDraftItemId(anchor: DraftAnchor): string {
  const { startLine, endLine } = draftAnchorSpan(anchor);
  // Single-line anchors keep the original ID shape so drafts saved before
  // ranges existed still resolve to the same item.
  const span = startLine === endLine ? `${endLine}` : `${startLine}-${endLine}`;
  return `line:${anchor.side}:${anchor.path}:${span}`;
}

export function lineAnchorForDraft(draft: DraftComment): DraftAnchor | undefined {
  if (draft.anchor?.kind === "line") return draft.anchor;
  const legacy = draft.itemId.match(/^line:(LEFT|RIGHT):(.+):(?:(\d+)-)?(\d+)$/);
  if (!legacy) return undefined;
  return {
    kind: "line",
    side: legacy[1] as "LEFT" | "RIGHT",
    path: legacy[2]!,
    line: Number(legacy[4]),
    ...(legacy[3] === undefined ? {} : { startLine: Number(legacy[3]) }),
  };
}

export interface PerformanceSample {
  id: string;
  fixtureId: string;
  name: string;
  valueMs: number;
  createdAt: string;
}

export interface QueueAction {
  id: string;
  fixtureId: string;
  /** New skips are scoped to the exact PR head; absent on legacy local records. */
  headSha?: string;
  type: "skip";
  reason: "not-my-area" | "too-large" | "needs-specialist" | "not-ready" | "guide-unclear";
  createdAt: string;
}

export interface ReviewSyncOutboxItem {
  id: string;
  localSessionId: string;
  remoteSessionId: string;
  event: ReviewEvent;
  createdAt: string;
}

/**
 * Active review time lives outside the session row on purpose: the heartbeat
 * writes every ten seconds, and a write to `sessions` re-fires every live
 * query on that table — which used to re-render the whole review workspace
 * on each tick.
 */
export interface ActiveTimeRecord {
  /** The owning session's id. */
  id: string;
  activeDurationMs: number;
}

class ReviewDatabase extends Dexie {
  fixtures!: EntityTable<CachedFixture, "id">;
  sessions!: EntityTable<LocalReviewSession, "id">;
  events!: EntityTable<ReviewEvent, "id">;
  drafts!: EntityTable<DraftComment, "id">;
  performance!: EntityTable<PerformanceSample, "id">;
  queueActions!: EntityTable<QueueAction, "id">;
  syncOutbox!: EntityTable<ReviewSyncOutboxItem, "id">;
  activeTime!: EntityTable<ActiveTimeRecord, "id">;

  constructor() {
    // Keep the original database name so the redesign does not discard existing attempts.
    super("watchful-review-guide");
    this.version(1).stores({
      fixtures: "id, cachedAt",
      sessions: "id, fixtureId, lastOpenedAt",
      events: "id, sessionId, createdAt, type",
      drafts: "id, sessionId, itemId, updatedAt",
      performance: "id, fixtureId, name, createdAt",
    });
    this.version(2).stores({
      fixtures: "id, cachedAt",
      sessions: "id, fixtureId, guideHeadSha, lastOpenedAt",
      events: "id, sessionId, createdAt, type",
      drafts: "id, sessionId, itemId, updatedAt",
      performance: "id, fixtureId, name, createdAt",
      queueActions: "id, fixtureId, type, reason, createdAt",
    });
    this.version(3).stores({
      fixtures: "id, cachedAt",
      sessions: "id, fixtureId, guideHeadSha, lastOpenedAt",
      events: "id, sessionId, createdAt, type",
      drafts: "id, sessionId, itemId, updatedAt",
      performance: "id, fixtureId, name, createdAt",
      queueActions: "id, fixtureId, type, reason, createdAt",
      syncOutbox: "id, localSessionId, remoteSessionId, createdAt",
    });
    this.version(4).stores({
      fixtures: "id, cachedAt",
      sessions: "id, fixtureId, guideHeadSha, lastOpenedAt",
      events: "id, sessionId, createdAt, type",
      drafts: "id, sessionId, itemId, updatedAt",
      performance: "id, fixtureId, name, createdAt",
      queueActions: "id, fixtureId, type, reason, createdAt",
      syncOutbox: "id, localSessionId, remoteSessionId, createdAt",
    });
    this.version(5).stores({
      fixtures: "id, cachedAt",
      sessions: "id, fixtureId, guideHeadSha, lastOpenedAt",
      events: "id, sessionId, createdAt, type",
      drafts: "id, sessionId, itemId, updatedAt",
      performance: "id, fixtureId, name, createdAt",
      queueActions: "id, fixtureId, type, reason, createdAt",
      syncOutbox: "id, localSessionId, remoteSessionId, createdAt",
      activeTime: "id",
    }).upgrade(async (transaction) => {
      const sessions = await transaction.table("sessions").toArray() as LocalReviewSession[];
      await transaction.table("activeTime").bulkPut(
        sessions
          .filter((session) => session.activeDurationMs)
          .map((session) => ({ id: session.id, activeDurationMs: session.activeDurationMs ?? 0 })),
      );
    });
  }
}

export const db = new ReviewDatabase();

export function sessionIdFor(fixtureId: string, headSha: string): string {
  return `local:${fixtureId}:${headSha}`;
}

export async function ensureSession(
  fixtureId: string,
  guideHeadSha: string,
  firstLayerId: string,
): Promise<LocalReviewSession> {
  const id = sessionIdFor(fixtureId, guideHeadSha);
  const existing = await db.sessions.get(id);
  const now = new Date().toISOString();
  if (existing) {
    const next = { ...existing, lastOpenedAt: now };
    await db.sessions.put(next);
    return next;
  }
  const created: LocalReviewSession = {
    id,
    fixtureId,
    guideHeadSha,
    startedAt: now,
    lastOpenedAt: now,
    activeLayerId: firstLayerId,
    activeDurationMs: 0,
  };
  // A new head starts a fresh session, but the reviewer's position and
  // per-step view state (collapsed files, selection, scroll) carry over from
  // the previous head, so a push does not forget what was already looked at.
  const previous = (await db.sessions.where("fixtureId").equals(fixtureId).sortBy("lastOpenedAt")).at(-1);
  if (previous?.layerViewStates) created.layerViewStates = previous.layerViewStates;
  if (previous?.activeLayerId && !previous.completedAt) {
    created.activeLayerId = previous.activeLayerId;
    created.activeFocusId = previous.activeFocusId;
  }
  // React StrictMode can initialise the same local session twice in development.
  // `put` keeps that startup idempotent while the stable session ID prevents duplicates.
  await db.sessions.put(created);
  return created;
}

export async function cacheGuide(
  descriptor: FixtureDescriptor,
  guide: ReviewGuide,
  config?: ChewConfig,
): Promise<void> {
  const existing = await db.fixtures.get(descriptor.id);
  await db.fixtures.put({
    id: descriptor.id,
    descriptor,
    guide,
    config: config ?? existing?.config,
    files: existing?.files,
    cachedAt: new Date().toISOString(),
  });
}

export async function cacheFiles(fixtureId: string, files: FixtureFiles): Promise<void> {
  const fixture = await db.fixtures.get(fixtureId);
  if (!fixture) throw new Error(`Cannot cache files before guide '${fixtureId}'`);
  await db.fixtures.put({ ...fixture, files, cachedAt: new Date().toISOString() });
}

export async function resetReview(fixtureId: string, headSha: string): Promise<void> {
  const sessionId = sessionIdFor(fixtureId, headSha);
  await db.transaction("rw", [db.sessions, db.events, db.drafts, db.performance, db.syncOutbox, db.activeTime], async () => {
    await db.sessions.delete(sessionId);
    await db.events.where("sessionId").equals(sessionId).delete();
    await db.drafts.where("sessionId").equals(sessionId).delete();
    await db.performance.where("fixtureId").equals(fixtureId).delete();
    await db.syncOutbox.where("localSessionId").equals(sessionId).delete();
    await db.activeTime.delete(sessionId);
  });
}
